/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { newServices, storageServices } from 'services'
import { Option } from 'types/conmon'
import { useDisclosure } from '@chakra-ui/react'
import { DataChartMoreType, DataChartType, InfoModalType } from 'types/chart'
import {
  WordCategoryChartResponse,
  WordCloudType,
  WordLesChartResponse,
  WordTimeLineChartResponse,
} from 'services/companyServices'
import { options, optionsPdf } from './constant'

export type NewsDetailWordMapProps = {
  year?: number
  quarter: number
  typeNews: boolean
  companyId?: number
  reportType: number
  companyName: string | undefined
  industryName: string | undefined
  isOpenDrawer: boolean
}

export interface FormValues {
  term: string
  sort_by: number
  quarters: string
  targetYear: string
  display_size: string
  analysis_menu: string
  multi_company: Option[]
  number_of_word: string
  targetDocument: string
}

type ModeSelectionValues = Pick<
  FormValues,
  | 'term'
  | 'sort_by'
  | 'quarters'
  | 'targetYear'
  | 'display_size'
  | 'multi_company'
  | 'number_of_word'
  | 'targetDocument'
>

export const useNewsDetailWordMap = (props: NewsDetailWordMapProps) => {
  const {
    year,
    quarter,
    typeNews,
    reportType,
    companyName,
    industryName,
    isOpenDrawer,
  } = props
  const { id } = useParams()
  const accessToken = storageServices.getAccessToken()
  const optionsAnalysisMenu = typeNews ? optionsPdf : options
  const abortControllerRef = useRef<AbortController>(new AbortController())
  const defaultTargetDocument = ['1', '2', '4', '5'].includes(
    String(reportType),
  )
    ? String(reportType)
    : '2'

  const createDefaultModeSelection = useCallback(
    (): ModeSelectionValues => ({
      term: '1',
      sort_by: 1,
      quarters: '',
      targetYear: '',
      display_size: '0',
      multi_company: [],
      number_of_word: '2',
      targetDocument: defaultTargetDocument,
    }),
    [defaultTargetDocument],
  )

  const modeSelectionsRef = useRef<Record<string, ModeSelectionValues>>({
    '0': createDefaultModeSelection(),
    '1': createDefaultModeSelection(),
    '2': createDefaultModeSelection(),
  })

  const formik = useFormik<FormValues>({
    initialValues: {
      term: '1',
      sort_by: 1,
      quarters: '',
      targetYear: '',
      display_size: '0',
      analysis_menu: '0',
      multi_company: [],
      number_of_word: '2',
      targetDocument: defaultTargetDocument,
    },
    /**
     *
     */
    onSubmit() {},
  })

  const { setFieldValue, values, setValues } = formik

  const [reloadChart, setReloadChart] = useState(false)
  const [valueSwitch, setValueSwitch] = useState(false)
  const [windowWidthSize, setWindowWidthSize] = useState(0)
  const [multiCompanyOptions, setMultiCompanyOptions] = useState<Option[]>([])

  const [infoModal, setInfoModal] = useState<InfoModalType>({
    isShowAll: false,
    typeChart: '',
    index: null,
  })

  const [dataChart, setDataChart] = useState<DataChartType>({
    wordLesChart: {
      data: [],
      dataEchart: [],
      hashed_data: '',
    },
    dataListWordCloud: {
      data: [],
      hashed_data: '',
    },
    dataTimeLine: {
      data: [],
      hashed_data: '',
    },
  })

  const [isLoadingDataChart, setIsLoadingDataChart] = useState(false)

  const [dataChartMore, setDataChartMore] = useState<DataChartMoreType>({
    loading: false,
    data: [],
    hashed_data: '',
  })

  const [dataChartMoreInit, setDataChartMoreInit] = useState<
    {
      title: string
      words_cloud: WordCloudType[]
    }[]
  >([])

  const [responseDataWordCloud, setResponseDataWordCloud] = useState<{
    option1: WordLesChartResponse | null
    option2: WordCategoryChartResponse | null
    option3: WordTimeLineChartResponse[]
  }>({
    option1: null,
    option2: null,
    option3: [],
  })

  const [valueExplanation, setValueExplanation] = useState<{
    loading: boolean
    data: string
    done: boolean
  }>({ loading: false, data: '', done: true })

  const {
    isOpen: isZoom,
    onOpen: onOpenZoom,
    onClose: onCloseZoom,
  } = useDisclosure()

  const displaySizeChart = React.useMemo(() => {
    if (values.analysis_menu === '0') return 1
    switch (values.display_size) {
      case '0':
        return 1
      case '1':
        return 2
      case '2':
        return 5

      default:
        return 2
    }
  }, [values.analysis_menu, values.display_size])

  const containerChartWidth = React.useMemo(() => {
    const width = isOpenDrawer
      ? (windowWidthSize - 64) / 2 - 32
      : windowWidthSize - 128
    const containerWidth =
      width / displaySizeChart -
      (16 * (displaySizeChart - 1)) / displaySizeChart

    return containerWidth
  }, [displaySizeChart, isOpenDrawer, windowWidthSize])

  const containerChartHeight = React.useMemo(
    () => containerChartWidth / 1.58,
    [containerChartWidth],
  )

  const isNotWordmap = React.useMemo(
    () =>
      !dataChartMore.data.length &&
      !dataChart.dataTimeLine.data.length &&
      !dataChart.dataListWordCloud.data.length &&
      !dataChart.wordLesChart.dataEchart.length &&
      !dataChart.wordLesChart.data.length,
    [
      dataChartMore.data.length,
      dataChart.dataTimeLine.data.length,
      dataChart.dataListWordCloud.data.length,
      dataChart.wordLesChart.dataEchart.length,
      dataChart.wordLesChart.data.length,
    ],
  )

  const fileType = useMemo(() => {
    switch (values.analysis_menu) {
      case '0':
        if (!typeNews) return 'file_text'
        return 'file_path'
      case '1':
        return 'company_industry'
      case '2':
        return 'company_year'
      default:
        return 'file_path'
    }
  }, [typeNews, values.analysis_menu])

  const payloadSendWebsocket = useMemo(() => {
    let payloadMoreChart = dataChartMore.data.map(item => ({
      [item.title]: item.words_cloud,
    }))

    const result = payloadMoreChart.reduce((acc, cur) => {
      return { ...acc, ...cur }
    }, {})

    switch (values.analysis_menu) {
      case '0':
        return {
          les_miserable: dataChart.wordLesChart.dataEchart,
          word_cloud: dataChart.wordLesChart.data,
        }

      case '1':
        const obj = dataChart.dataListWordCloud.data.reduce(
          (acc, { title, words_cloud }) => {
            acc[title] = words_cloud
            return acc
          },
          {} as Record<string, any>,
        )
        return {
          ...responseDataWordCloud.option2,
          words_cloud: {
            ...obj,
            ...result,
          },
        }

      case '2':
        return dataChart.dataTimeLine.data

      default:
        return {}
    }
  }, [
    dataChartMore.data,
    values.analysis_menu,
    dataChart.wordLesChart.dataEchart,
    dataChart.wordLesChart.data,
    dataChart.dataListWordCloud.data,
    dataChart.dataTimeLine.data,
    responseDataWordCloud.option2,
  ])

  const fetchDataChartNewsString = useCallback(
    async (id: string) => {
      try {
        const response = await newServices.getDataChartNewsString({
          news_id: +id,
          ngram: +values.number_of_word || 2,
          only_cache: true,
          fe_url: window.location.href,
        })

        if (
          response.is_success === false &&
          response.message === 'miss_cache'
        ) {
          setDataChart(prev => ({
            ...prev,
            wordLesChart: {
              dataEchart: [],
              data: [],
              hashed_data: '',
            },
            dataListWordCloud: {
              data: [],
              hashed_data: '',
            },
            dataTimeLine: {
              data: [],
              hashed_data: '',
            },
          }))
          setIsLoadingDataChart(false)
          return
        }

        const dataResponse = response.data as WordLesChartResponse
        const { hashed_data } = response

        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            data: dataResponse?.word_cloud ?? [],
            dataEchart: dataResponse?.les_miserable ?? [],
            hashed_data: hashed_data ?? '',
          },
          dataListWordCloud: {
            data: [],
            hashed_data: '',
          },
          dataTimeLine: {
            data: [],
            hashed_data: '',
          },
        }))

        setIsLoadingDataChart(false)

        setResponseDataWordCloud(prev => ({
          ...prev,
          option1: dataResponse,
        }))
      } catch (error) {
        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            dataEchart: [],
            data: [],
            hashed_data: '',
          },
          dataListWordCloud: {
            data: [],
            hashed_data: '',
          },
          dataTimeLine: {
            data: [],
            hashed_data: '',
          },
        }))
        setIsLoadingDataChart(false)
      }
    },
    [values.number_of_word],
  )

  const fetchWordCategoryChart = useCallback(
    async (id: string, year: number) => {
      try {
        const { data, hashed_data } = await newServices.getWordCategoryChart({
          year: year,
          news_id: +id,
          quarter: quarter,
          type_of_process: +values.targetDocument,
          ngram: +values.number_of_word,
        })

        const convertData = Object.entries(data.words_cloud)
          .filter(([, words]) => words.length)
          .map(([key, words]) => ({ title: key, words_cloud: words }))

        const arrCompany = data.companies_ability_to_compare.map(o => ({
          label: Object.keys(o)[0],
          value: o[Object.keys(o)[0]],
        }))

        setMultiCompanyOptions(arrCompany)
        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            data: [],
            dataEchart: [],
            hashed_data: '',
          },
          dataListWordCloud: {
            data: convertData,
            hashed_data: hashed_data ?? '',
          },
          dataTimeLine: {
            data: [],
            hashed_data: '',
          },
        }))
        setIsLoadingDataChart(false)

        setResponseDataWordCloud(prev => ({ ...prev, option2: data }))
      } catch (error) {
        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            dataEchart: [],
            data: [],
            hashed_data: '',
          },
          dataListWordCloud: {
            data: [],
            hashed_data: '',
          },
          dataTimeLine: {
            data: [],
            hashed_data: '',
          },
        }))
        setIsLoadingDataChart(false)
      }
    },
    [quarter, values.number_of_word, values.targetDocument],
  )

  const fetchWordCloudTimeLineChart = useCallback(
    async (id: string) => {
      try {
        const { data: data3, hashed_data: hashed_data_3 } =
          await newServices.getWordCloudTimeLineChart({
            year: year,
            news_id: +id,
            quarter: props.quarter,
            type_of_process: +values.targetDocument,
            previous_times: +values.term,
            ngram: +values.number_of_word,
          })

        const convertData = data3.sort(
          (a: { index: number }, b: { index: number }) => {
            if (values.sort_by === 1) {
              return a.index - b.index
            } else {
              return b.index - a.index
            }
          },
        )

        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            data: [],
            dataEchart: [],
            hashed_data: '',
          },
          dataListWordCloud: {
            data: [],
            hashed_data: '',
          },
          dataTimeLine: {
            data: convertData,
            hashed_data: hashed_data_3 ?? '',
          },
        }))
        setIsLoadingDataChart(false)

        setResponseDataWordCloud(prev => ({ ...prev, option3: data3 }))
      } catch (error) {
        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            dataEchart: [],
            data: [],
            hashed_data: '',
          },
          dataListWordCloud: {
            data: [],
            hashed_data: '',
          },
          dataTimeLine: {
            data: [],
            hashed_data: '',
          },
        }))
        setIsLoadingDataChart(false)
      }
    },
    [
      year,
      values.term,
      props.quarter,
      values.sort_by,
      values.number_of_word,
      values.targetDocument,
    ],
  )

  // fetchDataChart with news is string
  const fetchDataChart = useCallback(async () => {
    if (!id) return

    setDataChart(prev => ({
      ...prev,
      wordLesChart: {
        dataEchart: [],
        data: [],
        hashed_data: '',
      },
      dataListWordCloud: {
        data: [],
        hashed_data: '',
      },
      dataTimeLine: {
        data: [],
        hashed_data: '',
      },
    }))
    setIsLoadingDataChart(true)

    switch (values.analysis_menu) {
      case '0':
        fetchDataChartNewsString(id)
        break
      case '1':
        if (!year) return
        fetchWordCategoryChart(id, year)
        break
      case '2':
        if (!values.term) {
          setDataChart(prev => ({
            ...prev,
            wordLesChart: {
              dataEchart: [],
              data: [],
              hashed_data: '',
            },
            dataListWordCloud: {
              data: [],
              hashed_data: '',
            },
            dataTimeLine: {
              data: [],
              hashed_data: '',
            },
          }))
          setIsLoadingDataChart(false)

          break
        }
        fetchWordCloudTimeLineChart(id)
        break
      default:
        break
    }
  }, [
    id,
    year,
    values.term,
    values.analysis_menu,
    fetchWordCategoryChart,
    fetchDataChartNewsString,
    fetchWordCloudTimeLineChart,
  ])

  const fetchMoreWordCloud = useCallback(async () => {
    if (!id || !values.multi_company || !year) return

    // Compare the value selected with the data and remove outdated data
    const labelSet = new Set(values.multi_company.map(itm => itm.label))

    const presentValue = dataChartMore.data.filter(itm =>
      labelSet.has(itm.title),
    )

    const presentValueInit = dataChartMoreInit.filter(itm =>
      labelSet.has(itm.title),
    )

    if (values.multi_company.length === presentValue.length) {
      setDataChartMore(prev => ({ ...prev, data: presentValue }))
      setDataChartMoreInit(presentValueInit)
      return
    }

    // Find value selected but no data and get data of this value
    const dataSet = new Set(dataChartMore.data.map(itm => itm.title))
    const newCompanyId = values.multi_company
      .filter(itm => !dataSet.has(itm.label))
      .map(itm => itm.value)
    try {
      setDataChartMore(prev => ({
        ...prev,
        loading: true,
        hashed_data: '',
      }))

      const arrCompany = await Promise.all(
        newCompanyId.map(
          async item =>
            await newServices.getWordCloudMoreChart({
              year: year,
              news_id: +id,
              quarter: quarter,
              compare_to: +item,
              type_of_process: +values.targetDocument,
              ngram: +values.number_of_word,
            }),
        ),
      )

      let newData: {
        title: string
        words_cloud: { text: string; value: number }[]
      }[] = []
      let hashed_data = ''

      arrCompany.forEach(item => {
        let title = Object.keys(item.data)[0]

        let data = item.data[title].map(word => ({
          text: word.text,
          value: word.value,
        }))

        hashed_data = hashed_data + item.hashed_data
        newData = [...newData, { title: title, words_cloud: data }]
      })

      setDataChartMore(prev => ({
        ...prev,
        data: [...prev.data, ...newData],
        loading: false,
        hashed_data: hashed_data,
      }))
      setDataChartMoreInit(prev => [...prev, ...newData])
    } catch (error) {
      setDataChartMore(prev => ({
        ...prev,
        loading: false,
        data: [],
        hashed_data: '',
      }))
      setDataChartMoreInit([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    year,
    quarter,
    reportType,
    values.multi_company,
    values.number_of_word,
    values.targetDocument,
  ])

  const handleOffSwitch = () => {
    setValueSwitch(false)
    setValueExplanation(prev => ({
      ...prev,
      loading: false,
      data: '',
      done: true,
    }))

    abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()
  }

  const handleResetForm = (
    e:
      | {
          label: string
          value: string | number
        }
      | undefined,
  ) => {
    if (e?.value === values.analysis_menu) return

    const nextMode = String(e?.value)

    setValues(prev => {
      modeSelectionsRef.current[prev.analysis_menu] = {
        term: prev.term,
        sort_by: prev.sort_by,
        quarters: prev.quarters,
        targetYear: prev.targetYear,
        display_size: prev.display_size,
        multi_company: prev.multi_company,
        number_of_word: prev.number_of_word,
        targetDocument: prev.targetDocument,
      }

      return {
        ...prev,
        ...modeSelectionsRef.current[nextMode],
        analysis_menu: nextMode,
      }
    })
    handleResetDataChart()
    handleOffSwitch()
  }

  useEffect(() => {
    modeSelectionsRef.current[values.analysis_menu] = {
      term: values.term,
      sort_by: values.sort_by,
      quarters: values.quarters,
      targetYear: values.targetYear,
      display_size: values.display_size,
      multi_company: values.multi_company,
      number_of_word: values.number_of_word,
      targetDocument: values.targetDocument,
    }
  }, [
    values.analysis_menu,
    values.display_size,
    values.multi_company,
    values.number_of_word,
    values.quarters,
    values.sort_by,
    values.targetDocument,
    values.targetYear,
    values.term,
  ])

  const handleSwitch = async () => {
    if (!companyName || isNotWordmap) return

    if (valueSwitch) {
      handleOffSwitch()
    } else {
      if (valueExplanation.loading) return

      if (values.analysis_menu === '2' && !values.term) return

      if (isNotWordmap) return

      setValueSwitch(true)
      setReloadChart(!reloadChart)
      setValueExplanation(prev => ({
        ...prev,
        loading: true,
        data: '',
        done: false,
      }))
    }
  }

  const handleResetDataChart = () => {
    setDataChart(prev => ({
      ...prev,
      wordLesChart: {
        dataEchart: [],
        data: [],
        hashed_data: '',
      },
      dataListWordCloud: {
        data: [],
        hashed_data: '',
      },
      dataTimeLine: {
        data: [],
        hashed_data: '',
      },
    }))
    setIsLoadingDataChart(false)

    setDataChartMore(prev => ({
      ...prev,
      data: [],
      loading: false,
      hashed_data: '',
    }))
  }

  const handleOpenZoom = (info: {
    isShowAll: boolean
    typeChart: string
    index: number | null
  }) => {
    setInfoModal(info)
    onOpenZoom()
  }

  const handleCloseZoom = () => {
    onCloseZoom()
  }

  const handleSaveImproveWordCloudData = (
    type: string,
    index: number,
    data: WordCloudType[],
  ) => {
    switch (type) {
      case 'wordLesChart':
        setDataChart(prev => ({
          ...prev,
          wordLesChart: { ...prev.wordLesChart, data: data },
        }))
        break
      case 'dataListWordCloud':
        let newValue = dataChart.dataListWordCloud.data.map((itm, idx) =>
          idx === index ? { title: itm.title, words_cloud: data } : itm,
        )
        setDataChart(prev => ({
          ...prev,
          dataListWordCloud: { ...prev.dataListWordCloud, data: newValue },
        }))
        break

      case 'dataTimeLine':
        let newValue1 = dataChart.dataTimeLine.data.map((itm, idx) =>
          idx === index
            ? { index: itm.index, title: itm.title, words_cloud: data }
            : itm,
        )
        setDataChart(prev => ({
          ...prev,
          dataTimeLine: { ...prev.dataTimeLine, data: newValue1 },
        }))
        break
      case 'dataChartMore':
        let newValue2 = dataChartMore.data.map((itm, idx) =>
          idx === index - dataChart.dataListWordCloud.data.length
            ? { title: itm.title, words_cloud: data }
            : itm,
        )
        setDataChartMore(prev => ({
          ...prev,
          data: newValue2,
        }))
        break

      default:
        break
    }
  }

  const handleBackToInitialWordCloudData = (type: string, index: number) => {
    switch (type) {
      case 'wordLesChart':
        setDataChart(prev => ({
          ...prev,
          wordLesChart: {
            ...prev.wordLesChart,
            data: responseDataWordCloud.option1?.word_cloud ?? [],
          },
        }))
        break
      case 'dataListWordCloud':
        const convertData = Object.entries(
          responseDataWordCloud.option2?.words_cloud ?? {},
        )
          .filter(([, words]) => words.length)
          .map(([key, words]) => ({ title: key, words_cloud: words }))

        const findData = convertData.find((itm, idx) => idx === index)

        if (findData) {
          let newValue = dataChart.dataListWordCloud.data.map((itm, idx) =>
            idx === index
              ? { title: itm.title, words_cloud: findData.words_cloud }
              : itm,
          )
          setDataChart(prev => ({
            ...prev,
            dataListWordCloud: { ...prev.dataListWordCloud, data: newValue },
          }))
        }
        break
      case 'dataTimeLine':
        const findData3 = responseDataWordCloud.option3.find(
          (itm, idx) => idx === index,
        )
        if (findData3) {
          const newValue3 = dataChart.dataTimeLine.data.map((itm, idx) =>
            idx === index
              ? {
                  index: itm.index,
                  title: itm.title,
                  words_cloud: findData3.words_cloud,
                }
              : itm,
          )
          setDataChart(prev => ({
            ...prev,
            dataTimeLine: { ...prev.dataTimeLine, data: newValue3 },
          }))
        }
        break
      case 'dataChartMore':
        const findData4 = dataChartMoreInit.find(
          (itm, idx) => idx + dataChart.dataListWordCloud.data.length === index,
        )

        if (findData4) {
          let newValue4 = dataChartMore.data.map((itm, idx) =>
            idx + dataChart.dataListWordCloud.data.length === index
              ? { title: itm.title, words_cloud: findData4.words_cloud }
              : itm,
          )
          setDataChartMore(prev => ({ ...prev, data: newValue4 }))
        }
        break

      default:
        break
    }
  }

  useEffect(() => {
    fetchMoreWordCloud()
  }, [fetchMoreWordCloud])

  useEffect(() => {
    fetchDataChart()
  }, [fetchDataChart])

  const hashedDataChart = useMemo(() => {
    switch (values.analysis_menu) {
      case '0':
        return dataChart.wordLesChart.hashed_data

      case '1':
        return (
          dataChart.dataListWordCloud.hashed_data + dataChartMore.hashed_data
        )

      case '2':
        return dataChart.dataTimeLine.hashed_data

      default:
        return ''
    }
  }, [
    values.analysis_menu,
    dataChartMore.hashed_data,
    dataChart.dataTimeLine.hashed_data,
    dataChart.wordLesChart.hashed_data,
    dataChart.dataListWordCloud.hashed_data,
  ])

  useEffect(() => {
    if (!accessToken || isNotWordmap || !valueSwitch) return

    const newSocket = new WebSocket(
      `${process.env.REACT_APP_WEBSOCKET_URL}/api/v1/advisor/list_new_file/summary_image/ws_stream`,
    )

    const handleSocketOpen = async () => {
      console.log('WS connected')
      const messAuthen = {
        type: 'auth_req',
        data: { Authorization: `Bearer ${accessToken}` },
      }
      newSocket.send(JSON.stringify(messAuthen))

      let message = {
        type: 'stream_req',
        data: {
          data: payloadSendWebsocket,
          company: companyName,
          type_image: fileType,
          hashed_data: hashedDataChart,
          type_of_process: +values.targetDocument,
        },
      }

      newSocket.send(JSON.stringify(message))
    }

    const handleSocketMessage = (event: { data: string }) => {
      const objMess = JSON.parse(event.data)

      if (objMess?.done === 1) {
        setValueExplanation(prev => ({
          ...prev,
          loading: false,
          done: true,
          data: prev.data.replace(/```/g, ''),
        }))
      } else {
        switch (objMess.type) {
          case 'stream_res':
            setValueExplanation(prev => ({
              ...prev,
              loading: false,
              data: (prev.data + objMess?.data).replace(/```html/g, ''),
            }))
            break
          case 'error_res':
            setValueExplanation(prev => ({
              ...prev,
              loading: false,
              done: true,
              data: prev.data || '',
            }))
            break
          default:
            break
        }
      }
    }

    const handleSocketClose = () => {
      console.log('WebSocket is disconnected')
      setValueExplanation(prev => ({
        ...prev,
        loading: false,
        done: true,
      }))
    }

    const handleSocketError = () => {
      console.log('WebSocket error')
      setValueExplanation(prev => ({
        ...prev,
        loading: false,
        done: true,
      }))
    }

    newSocket.addEventListener('open', handleSocketOpen)
    newSocket.addEventListener('close', handleSocketClose)
    newSocket.addEventListener('message', handleSocketMessage)
    newSocket.addEventListener('error', handleSocketError)

    return () => {
      newSocket.removeEventListener('open', handleSocketOpen)
      newSocket.removeEventListener('close', handleSocketClose)
      newSocket.removeEventListener('message', handleSocketMessage)
      newSocket.removeEventListener('error', handleSocketError)
      newSocket.close()
    }
  }, [
    fileType,
    valueSwitch,
    accessToken,
    companyName,
    isNotWordmap,
    hashedDataChart,
    payloadSendWebsocket,
    values.analysis_menu,
    values.targetDocument,
  ])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidthSize(document.documentElement.clientWidth)
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (isOpenDrawer) {
      setFieldValue('display_size', '0')
    } else {
      setFieldValue('display_size', '1')
    }
  }, [isOpenDrawer, setFieldValue])

  return {
    ...props,
    formik,
    isZoom,
    values,
    typeNews,
    dataChart,
    infoModal,
    reportType,
    valueSwitch,
    isNotWordmap,
    isOpenDrawer,
    dataChartMore,
    displaySizeChart,
    valueExplanation,
    isLoadingDataChart,
    containerChartWidth,
    multiCompanyOptions,
    optionsAnalysisMenu,
    containerChartHeight,
    handleSwitch,
    setDataChart,
    setFieldValue,
    handleOpenZoom,
    handleResetForm,
    handleOffSwitch,
    handleCloseZoom,
    setDataChartMore,
    handleResetDataChart,
    handleSaveImproveWordCloudData,
    handleBackToInitialWordCloudData,
  }
}
